/** Zelfde logo’s als op https://dominicusamsterdam.nl (publieke HTTPS-URL’s; geschikt voor e-mail). */
export const DOMINICUS_LOGO_URL =
  "https://dominicusamsterdam.nl/wp-content/uploads/2023/08/Dominicus-logo.svg";
export const DOMINICUS_FAVICON_URL =
  "https://dominicusamsterdam.nl/wp-content/uploads/2023/06/cropped-favicon-450x450.png";

/** Ingebouwde sleutels; custom rollen gebruiken dezelfde kolom met een eigen sleutel. */
export type BuiltinAppRole =
  | "deelnemer"
  /** Alleen-lezen: alle adresboeklijsten; alleen naam + e-mail + telefoon (geen adres, notities, enz.). */
  | "lezer_basis"
  /** Alleen-lezen: alle adresboeklijsten; volledige kaartweergave (geen bewerken). */
  | "lezer_volledig"
  | "secretaris"
  | "vrijwilliger_coordinator"
  | "beheerder";

export type AppRole = BuiltinAppRole | (string & {});

/** Vooringesteld account (nog geen Microsoft-inlog): `microsoft_oid` begint hiermee. */
export const PENDING_MICROSOFT_OID_PREFIX = "PENDING:" as const;

export function isPendingMicrosoftUserOid(oid: string | null | undefined): boolean {
  return typeof oid === "string" && oid.startsWith(PENDING_MICROSOFT_OID_PREFIX);
}

export function normalizeUserEmailForMatch(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Placeholder voor `group_werkgroep_intakes.recipient_email` als er bij link-aanmaak geen voorzitter-mail is.
 * Geen echte mailbox (`.invalid` is gereserveerd, RFC 6761). Goedkeur-/afwijs-/revisie-mail naar dit adres wordt overgeslagen.
 */
export const WERKGROEP_INTAKE_PLACEHOLDER_RECIPIENT_EMAIL = "noreply@werkgroep-intake.invalid" as const;

export function isWerkgroepIntakePlaceholderRecipientEmail(email: string | null | undefined): boolean {
  return normalizeUserEmailForMatch(email ?? "") === WERKGROEP_INTAKE_PLACEHOLDER_RECIPIENT_EMAIL;
}

/**
 * Standaard geldigheid van werkgroep-ledenlijst-intakelinks (dagen vanaf aanmaken).
 * Geldt voor `group_werkgroep_intakes.expires_at` en het bijbehorende geheim in `group_werkgroep_intake_secrets`.
 */
export const WERKGROEP_INTAKE_LINK_VALIDITY_DAYS = 30;

/** Eigen organisatie-domein voor werk-/Dominicus-mailboxen (niet primair/secundair privé). */
export const DOMINICUS_ORGANISATION_EMAIL_DOMAIN = "dominicusamsterdam.nl";

export function isDominicusOrganisationEmail(email: string | null | undefined): boolean {
  const e = normalizeUserEmailForMatch(email ?? "");
  return e.length > 0 && e.endsWith(`@${DOMINICUS_ORGANISATION_EMAIL_DOMAIN}`);
}

/**
 * Eén adres voor uitnodigingen / groepsmail: eerst het Dominicus-veld, dan primair (`email_prive` of legacy `email`),
 * dan secundair (`email_werk`). Lege of kennelijk ongeldige waarden worden overgeslagen.
 */
export function contactPreferredEmail(c: {
  email?: string;
  email_prive?: string;
  email_werk?: string;
  dominicus_email?: string;
}): string | undefined {
  const dom = (c.dominicus_email ?? "").trim();
  if (dom.includes("@")) return dom;
  const primary = (c.email_prive || c.email || "").trim();
  if (primary.includes("@")) return primary;
  const sec = (c.email_werk ?? "").trim();
  if (sec.includes("@")) return sec;
  return undefined;
}

/** Zelfde volgorde als {@link contactPreferredEmail}, voor koppeling met app-gebruiker (alle varianten, dedupe op lowercase). */
export function contactEmailMatchKeys(c: {
  email?: string;
  email_prive?: string;
  email_werk?: string;
  dominicus_email?: string;
}): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of [
    (c.dominicus_email ?? "").trim(),
    (c.email_prive || c.email || "").trim(),
    (c.email_werk ?? "").trim(),
  ]) {
    if (!raw.includes("@")) continue;
    const k = normalizeUserEmailForMatch(raw);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

/**
 * Eén e-mailregel voor kaart/rij: als er een @dominicusamsterdam.nl-adres is (veld Dominicus of nog in primair/secundair),
 * tonen we dat; anders primair, anders secundair, anders overige Dominicus-waarde.
 */
export function contactTileEmailLine(c: {
  email?: string;
  email_prive?: string;
  email_werk?: string;
  dominicus_email?: string;
}): string | undefined {
  const dom = (c.dominicus_email ?? "").trim();
  if (dom && isDominicusOrganisationEmail(dom)) return dom;
  const p = (c.email_prive || c.email || "").trim();
  if (p && isDominicusOrganisationEmail(p)) return p;
  const w = (c.email_werk ?? "").trim();
  if (w && isDominicusOrganisationEmail(w)) return w;
  return p || w || dom || undefined;
}

/** Zelfde vorm als in de database voor vooringestelde accounts. */
export function makePendingMicrosoftOid(email: string): string {
  return PENDING_MICROSOFT_OID_PREFIX + normalizeUserEmailForMatch(email);
}

import { userHasPermission } from "./userPermissions";

export interface User {
  id: number;
  microsoft_oid: string;
  name: string;
  email: string;
  avatar_url?: string;
  created_at: string;
  is_super_admin?: boolean;
  contact_id?: number | null;
  app_role?: AppRole;
  /** Rechten uit rollenmatrix; gezet door de server op /api/me. */
  permissions?: Partial<Record<import("./rolePermissions").AppPermissionKey, boolean>>;
}

/** Keys for the e-mail distribution list; labels are Dutch in the UI. Meerdere tegelijk via `email_lijsten`. */
export type EmailLijstKey = "alle_email" | "informatief" | "overlijden" | "40_dagen" | "geen";

/** How a DomAddress list maps to a MailerLite group. */
export type MailerLiteConnectionSourceType = "email_lijst" | "entity_kind" | "selection";

/** Saved filters when pushing contacts for an entity_kind / selection connection. */
export interface MailerLiteConnectionFilter {
  search?: string;
  group_id?: string;
  city?: string;
  country?: string;
  entity_kind?: string;
  group_activity?: "all" | "none" | "with_role";
  /** Only contacts with a marketing e-mail address (default true). */
  require_email?: boolean;
  /** Only contacts with at least one e-maillijst subscription (marketing consent). */
  require_marketing_consent?: boolean;
}

export interface MailerLiteConnection {
  id: number;
  ml_group_id: string;
  ml_group_name: string;
  source_type: MailerLiteConnectionSourceType;
  source_key: string | null;
  filter: MailerLiteConnectionFilter | null;
  membership_label: string | null;
  two_way: boolean;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export type MailerLiteConnectionPushResult = {
  examined: number;
  synced: number;
  skipped: number;
  failed: number;
  errors: string[];
};

export type MailerLiteEnsureWebhookResult = {
  created: boolean;
  webhook_id: string | null;
  secret_stored: boolean;
  message: string;
};

/** Short info for list header badge (id + group name). */
export type MailerLiteListConnectionSummary = {
  id: number;
  ml_group_name: string;
};

/**
 * Bezoekersbestand entry (visitor register).
 * Na de unified-contacts migratie is dit een view op `contacts` met entity_kind='dominicusganger'.
 * Veldnamen zijn API-compat gehouden (email, email_secondary, phone_secondary).
 */
export interface BezoekersbestandEntry {
  id: number;
  first_name: string;
  last_name: string;
  /** Geboortedatum: jaar (JJJJ), jaar-maand (JJJJ-MM), of volledige datum (JJJJ-MM-DD). */
  birth_date?: string;
  /** Overlijdensdatum: zelfde vormen als geboortedatum. */
  death_date?: string;
  kwalificaties?: string;
  /** E-mail primair (maps to contacts.email_prive) */
  email?: string;
  /** E-mail secundair (maps to contacts.email_werk) */
  email_secondary?: string;
  /** Telefoon primair */
  phone?: string;
  /** Telefoon secundair (maps to contacts.phone_mobile) */
  phone_secondary?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  notes?: string;
  wants_newsletter: boolean;
  wants_activities_info: boolean;
  willing_volunteer: boolean;
  /** Zelfde lijst-keys als bij medewerkers (meerdere). */
  email_lijsten?: EmailLijstKey[];
  /** @dominicusamsterdam.nl e.d.; zelfde betekenis als bij medewerkerskaarten. */
  dominicus_email?: string | null;
  /** Akkoord met algemene voorwaarden / privacy (AVG). */
  agv_ingestemd?: boolean;
  tussenvoegsel?: string;
  initialen?: string;
  notitie?: string;
  notities?: string;
  extra?: string;
  bellijn?: boolean;
  berichten?: boolean;
  /** Na migratie altijd null: promotie verandert entity_kind, er is geen aparte contacts-rij meer. */
  linked_contact_id?: number | null;
  created_by: number;
  created_at: string;
  updated_at: string;
}

/**
 * In welke lijst staat de persoon.
 * personeel      = betaald personeel
 * vrijwilliger   = vrijwilliger (voorheen "medewerker")
 * dominicusganger = kerkganger (voorheen "bezoeker" / bezoekersbestand)
 * extern         = externe contacten
 * leverancier    = leveranciers & zakelijke contacten
 */
export type ContactEntityKind = "personeel" | "vrijwilliger" | "dominicusganger" | "extern" | "leverancier" | "emaillijst";

/** Groepsrollen voor vrijwilliger/personeel-lijsten (server berekent per kaart). */
export interface ContactGroupActivity {
  /** Lid van minstens één groep (`contact_groups`). */
  is_group_member: boolean;
  /** Voorzitter via kaart of gekoppeld Microsoft-account. */
  is_group_chair: boolean;
  is_group_admin: boolean;
  is_group_viewer: boolean;
  /** Eén van bovenstaande (incl. beheer/kijker). */
  has_any_group_role: boolean;
}

export interface Contact {
  id: number;
  first_name: string;
  last_name: string;
  tussenvoegsel?: string;
  initialen?: string;
  /** Geboortedatum: jaar, jaar-maand, of volledige datum (JJJJ / JJJJ-MM / JJJJ-MM-DD). */
  birth_date?: string;
  /** Overlijdensdatum: zelfde vormen. */
  death_date?: string;
  /** Meerregelige opleiding / titels e.d. */
  kwalificaties?: string;
  /** Bedrijfs- of organisatienaam; vooral bij externe contacten en leveranciers. */
  bedrijfsnaam?: string | null;
  /** Legacy; prefer email_prive */
  email?: string;
  email_prive?: string;
  email_werk?: string;
  /** E-mail op het eigen organisatiedomein (bijv. @dominicusamsterdam.nl); los van primair/secundair. */
  dominicus_email?: string;
  email_lijst?: EmailLijstKey;
  /** Actieve mailinglijsten (meerdere); leeg = geen. */
  email_lijsten?: EmailLijstKey[];
  /** Telefoon primair (vast of algemeen) */
  phone?: string;
  /** Telefoon secundair (bijv. mobiel) */
  phone_mobile?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  /** Legacy; use notities where possible */
  notes?: string;
  notitie?: string;
  notities?: string;
  extra?: string;
  bellijn?: boolean;
  berichten?: boolean;
  /** Akkoord met de algemene voorwaarden / privacy (AVG). */
  agv_ingestemd?: boolean;
  profile_picture?: string;
  /** Filled when this contact was created from (or linked to) a Bezoekersbestand row */
  bezoekersbestand_id?: number | null;
  entity_kind?: ContactEntityKind;
  /** App-brede rol; voor medewerkers leidend voor rechten, ook vóór Microsoft-inlog. */
  app_role?: AppRole;
  /** Dominicusganger-specifieke voorkeuren (alleen bij entity_kind = 'dominicusganger'). */
  wants_newsletter?: boolean;
  wants_activities_info?: boolean;
  willing_volunteer?: boolean;
  /** Vrije tags/lijsten (b.v. "personeel", "vrijwilligers"); via contact_labels tabel. */
  labels?: string[];
  /** Alleen bij API-responses: UI en beperkte velden voor leesrollen. */
  _meta?: { read_only?: boolean; read_scope?: "basis" | "volledig" };
  created_by: number;
  created_at: string;
  updated_at: string;
  groups?: Group[];
  /** Alleen lijst-API voor vrijwilliger/personeel: rollen in groepen. */
  group_activity?: ContactGroupActivity;
}

/** Externe contacten en leveranciers: persoonsnaam is niet altijd bekend. */
export function isOrganizationContactKind(kind: string | null | undefined): boolean {
  return kind === "extern" || kind === "leverancier";
}

/** Minstens één herkenbaar gegeven op de kaart (naam, bedrijf of bereikbaarheid). */
export function hasContactCardIdentity(c: {
  first_name?: string | null;
  last_name?: string | null;
  bedrijfsnaam?: string | null;
  email?: string | null;
  email_prive?: string | null;
  email_werk?: string | null;
  dominicus_email?: string | null;
  phone?: string | null;
  phone_mobile?: string | null;
}): boolean {
  const t = (s?: string | null) => (s ?? "").trim();
  return Boolean(
    t(c.first_name) ||
      t(c.last_name) ||
      t(c.bedrijfsnaam) ||
      t(c.email) ||
      t(c.email_prive) ||
      t(c.email_werk) ||
      t(c.dominicus_email) ||
      t(c.phone) ||
      t(c.phone_mobile)
  );
}

/**
 * Volledige weergavenaam: voornaam, optioneel tussenvoegsel, achternaam (Nederlandse volgorde).
 * Bij alleen bedrijfsnaam (extern/leverancier) wordt die getoond.
 */
export function formatContactDisplayName(c: {
  first_name?: string | null;
  tussenvoegsel?: string | null;
  last_name?: string | null;
  bedrijfsnaam?: string | null;
}): string {
  const parts = [c.first_name, c.tussenvoegsel, c.last_name]
    .map((s) => (s == null ? "" : String(s).trim()))
    .filter((s) => s !== "");
  const person = parts.join(" ");
  const org = (c.bedrijfsnaam ?? "").trim();
  if (person && org) return `${person} (${org})`;
  if (person) return person;
  return org || "—";
}

/** Kaart heeft een ingevulde overlijdensdatum (YYYY-MM-DD of andere niet-lege string). */
export function contactIsDeceased(c: { death_date?: string | null }): boolean {
  const d = c.death_date;
  return typeof d === "string" && d.trim() !== "";
}

/** Rol van deze persoon in de groep (alleen op contact-lijst API, bij `groups[]`). */
export type ContactGroupMembershipRole = "voorzitter" | "lid";

export interface Group {
  id: number;
  name: string;
  /** Lid of voorzitter in deze groep (alleen bij contact-lijst responses). */
  role?: ContactGroupMembershipRole;
  description?: string;
  /** Optioneel: groeps- of afdelingsmailadres */
  email?: string | null;
  created_by: number;
  created_at: string;
  updated_at: string;
  members?: Contact[];
  admins?: User[];
  viewers?: User[];
  /** Group voorzitter (max one); may be null */
  chair?: User | null;
  member_count?: number;
}

export interface ContactGroup {
  contact_id: number;
  group_id: number;
}

export interface GroupAdmin {
  group_id: number;
  user_id: number;
}

export interface ShareRequest {
  contact_ids: number[];
  recipient_emails: string[];
  message?: string;
}

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
}

export type ContactFormData = Omit<Contact, 'id' | 'created_by' | 'created_at' | 'updated_at' | 'groups' | 'profile_picture' | 'bezoekersbestand_id'> & {
  profile_picture?: File | string;
};

export type BezoekersbestandFormData = Omit<
  BezoekersbestandEntry,
  "id" | "created_by" | "created_at" | "updated_at" | "linked_contact_id"
>;

export type GroupFormData = Pick<Group, "name" | "description" | "email">;

/** Vaste lijst relaties: “de ander” t.o.v. deze kaart (uitgaand: “X is jouw moeder”). */
export const CONTACT_RELATION_CODES = [
  "moeder",
  "vader",
  "partner",
  /** Was partnerschap; beide kaarten tonen dezelfde status (symmetrisch). */
  "gescheiden",
  /** Partner is overleden; symmetrische koppeling op beide kaarten. */
  "partner_overleden",
  "zoon",
  "dochter",
  "broer",
  "zus",
  "kleinkind",
  "grootouder",
  "vriend",
  "vriendin",
  "collega",
  "familie_overig",
  "overig",
] as const;

export type ContactRelationCode = (typeof CONTACT_RELATION_CODES)[number];

/** Nederlandse labels voor UI en audit. */
export const CONTACT_RELATION_LABELS_NL: Record<ContactRelationCode, string> = {
  moeder: "Moeder",
  vader: "Vader",
  partner: "Partner",
  gescheiden: "Gescheiden (ex-partner)",
  partner_overleden: "Partner overleden",
  zoon: "Zoon",
  dochter: "Dochter",
  broer: "Broer",
  zus: "Zus",
  kleinkind: "Kleinkind",
  grootouder: "Grootouder",
  vriend: "Vriend",
  vriendin: "Vriendin",
  collega: "Collega",
  familie_overig: "Familie (overig)",
  overig: "Overig",
};

/**
 * Hoe de omgekeerde relatie op de *andere* kaart wordt bepaald:
 * - `symmetric`: zelfde code (partner ↔ partner)
 * - `fixed`: één vaste code (kleinkind ↔ grootouder)
 * - `choose`: gebruiker kiest (moeder ↔ zoon of dochter)
 */
export type RelationReverseRule =
  | { mode: "symmetric" }
  | { mode: "fixed"; reverse: ContactRelationCode }
  | { mode: "choose"; reverseOptions: readonly ContactRelationCode[] };

/** Per primaire relatiesoort: welke regel voor de automatische omgekeerde rij. */
export const RELATION_REVERSE_RULES: Record<ContactRelationCode, RelationReverseRule> = {
  moeder: { mode: "choose", reverseOptions: ["zoon", "dochter"] },
  vader: { mode: "choose", reverseOptions: ["zoon", "dochter"] },
  partner: { mode: "symmetric" },
  gescheiden: { mode: "symmetric" },
  partner_overleden: { mode: "symmetric" },
  zoon: { mode: "choose", reverseOptions: ["moeder", "vader"] },
  dochter: { mode: "choose", reverseOptions: ["moeder", "vader"] },
  broer: { mode: "choose", reverseOptions: ["broer", "zus"] },
  zus: { mode: "choose", reverseOptions: ["broer", "zus"] },
  kleinkind: { mode: "fixed", reverse: "grootouder" },
  grootouder: { mode: "fixed", reverse: "kleinkind" },
  vriend: { mode: "choose", reverseOptions: ["vriend", "vriendin", "partner"] },
  vriendin: { mode: "choose", reverseOptions: ["vriend", "vriendin", "partner"] },
  collega: { mode: "symmetric" },
  familie_overig: { mode: "choose", reverseOptions: ["familie_overig", "overig"] },
  overig: { mode: "symmetric" },
};

export function relationNeedsReverseChoice(primary: ContactRelationCode): boolean {
  return RELATION_REVERSE_RULES[primary].mode === "choose";
}

export function getReverseChoiceOptions(primary: ContactRelationCode): ContactRelationCode[] {
  const r = RELATION_REVERSE_RULES[primary];
  return r.mode === "choose" ? [...r.reverseOptions] : [];
}

/**
 * Bepaalt de relatiesoort op de *andere* kaart (`reverse_relation_code` alleen bij `choose`).
 */
/** Korte NL-label voor automatische omgekeerde relatie (symmetrisch of vast). */
export function getAutoReverseRelationLabel(primary: ContactRelationCode): string | null {
  const rule = RELATION_REVERSE_RULES[primary];
  if (rule.mode === "symmetric") return CONTACT_RELATION_LABELS_NL[primary];
  if (rule.mode === "fixed") return CONTACT_RELATION_LABELS_NL[rule.reverse];
  return null;
}

/** Partner / ex / overleden: onderling te wijzigen (PATCH) en symmetrisch bij nieuwe koppeling. */
export const PARTNER_LIFECYCLE_CODES = ["partner", "gescheiden", "partner_overleden"] as const;
export type PartnerLifecycleRelationCode = (typeof PARTNER_LIFECYCLE_CODES)[number];

export function isPartnerLifecycleRelationCode(c: string): c is PartnerLifecycleRelationCode {
  return (PARTNER_LIFECYCLE_CODES as readonly string[]).includes(c);
}

export function resolveReverseRelationCode(
  primary: ContactRelationCode,
  chosenReverse: ContactRelationCode | undefined | null
): { ok: true; reverse: ContactRelationCode } | { ok: false; error: string } {
  const rule = RELATION_REVERSE_RULES[primary];
  if (rule.mode === "symmetric") {
    return { ok: true, reverse: primary };
  }
  if (rule.mode === "fixed") {
    return { ok: true, reverse: rule.reverse };
  }
  const rev = chosenReverse ?? undefined;
  if (rev == null || !rule.reverseOptions.includes(rev)) {
    return { ok: false, error: "Kies hoe jij op de andere kaart genoemd wordt (omgekeerde relatie)." };
  }
  return { ok: true, reverse: rev };
}

/** Eén relatie-regel voor GET /…/relations (uitgaand of binnenkomend). */
export interface ContactRelationRow {
  id: number;
  direction: "out" | "in";
  other_contact_id: number;
  other_first_name: string;
  other_last_name: string;
  relation_code: ContactRelationCode;
}

/** Taak gekoppeld aan een groepslid (herbruikbare naam uit `taken`). */
export interface GroupMemberTaken {
  id: number;
  name: string;
}

/** Wie de activiteitenlog in Beheer mag zien: super, organisatiebeheerder of secretaris. */
export function canViewActivityLog(user: User | null | undefined): boolean {
  return userHasPermission(user, "view_activity_log");
}

/** Volledig adresboek (alle contacttypes), o.a. GET /api/contacts — niet voor lezers. */
export function hasOrgWideContactDirectoryAccess(user: User | null | undefined): boolean {
  return userHasPermission(user, "org_contacts_edit");
}

/**
 * Alle kaarten in lijsten (lezen), incl. lezer-rollen.
 * Gebruikt niet voor dominicusgangers (die via dominicusgangers_*-rechten).
 */
export function hasOrgWideContactReadAccess(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.is_super_admin) return true;
  return (
    userHasPermission(user, "org_contacts_read_basis") ||
    userHasPermission(user, "org_contacts_read_full") ||
    userHasPermission(user, "org_contacts_edit")
  );
}

/** Organisatie-brede alleen-lezen rollen op adresboekkaarten (geen POST/PUT/DELETE). */
export function isOrgContactReadOnlyRole(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.is_super_admin) return false;
  if (userHasPermission(user, "org_contacts_edit")) return false;
  return userHasPermission(user, "org_contacts_read_basis") || userHasPermission(user, "org_contacts_read_full");
}

export {
  parseBezoekerFormEmail,
  parseBezoekerFormBlock,
  parseConsentPhrase,
  splitBezoekerFormBlocks,
  type ParsedBezoekerForm,
  type ParseBlockResult,
} from "./parseBezoekerFormEmail";

/** API: welke veldenbeperking geldt voor deze gebruiker (`null` = normale kaart, geen extra _meta nodig). */
export function getContactReadScopeForApi(user: User | null | undefined): "basis" | "volledig" | null {
  if (!user) return null;
  if (user.is_super_admin || userHasPermission(user, "org_contacts_edit")) return null;
  if (userHasPermission(user, "org_contacts_read_full")) return "volledig";
  if (userHasPermission(user, "org_contacts_read_basis")) return "basis";
  return null;
}

export function canDuplicateContactCard(user: User | null | undefined): boolean {
  return userHasPermission(user, "contacts_duplicate");
}

export function canViewGroupSecretary(user: User | null | undefined): boolean {
  return userHasPermission(user, "groups_view_secretary");
}

export function canManageGroupSecretary(user: User | null | undefined): boolean {
  return userHasPermission(user, "groups_manage_secretary");
}

export * from "./rolePermissions";
export * from "./userPermissions";
export * from "./agenda";
export * from "./emailImportNormalize";
export * from "./contactEmailOptions";
export * from "./emailToFieldFormat";
